import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { ExternalLink } from "lucide-react";

/**
 * Gadgets page - showcasing tools and gear
 * Clean minimal design with product cards
 */

// Amazon affiliate tag
const AMAZON_TAG = "limkc66999-22";
const amazonLink = (productId) => `https://www.amazon.com/dp/${productId}?tag=${AMAZON_TAG}`;

// Gear data organized by category
const gearData = {
  desk: {
    title: "Workstation",
    items: [
      {
        name: "ASUS ROG RTX 4090",
        description: "24GB Graphics Card",
        image: "/IMAGE/gadgets/gadget_22.jpg",
        link: amazonLink("B0DS2WQZ2M")
      },
      {
        name: "AMD Ryzen 9 9950X",
        description: "16-Core 32-Thread Processor",
        image: "/IMAGE/gadgets/gadget_21.jpg",
        link: amazonLink("B0D6NNRBGP")
      },
      {
        name: "MacBook Pro 14\"",
        description: "M4 Pro, 48GB RAM",
        image: "https://store.storeimages.cdn-apple.com/4982/as-images.apple.com/is/mbp14-spacegray-select-202310?wid=400&hei=400&fmt=jpeg&qlt=90",
        link: amazonLink("B0CM5JV268")
      },
      {
        name: "ASUS ROG Strix 49\" Ultrawide",
        description: "XG49VQ Curved Gaming Monitor",
        image: "/IMAGE/gadgets/gadget_2.jpg",
        link: amazonLink("B07PZR2YY4")
      },
      {
        name: "ErGear Standing Desk",
        description: "Electric Height Adjustable",
        image: "/IMAGE/gadgets/gadget_3.jpg",
        link: amazonLink("B0B41Z34M7")
      },
      {
        name: "LiberNovo Omni Chair",
        description: "Dynamic Ergonomic Office Chair",
        image: "/IMAGE/gadgets/gadget_1.jpg",
        link: amazonLink("B0FYCKPWP9")
      }
    ]
  },
  kindle: {
    title: "Kindle",
    items: [
      {
        name: "AWS Certified Guide",
        description: "Solutions Architect Study Guide",
        image: "/IMAGE/gadgets/gadget_4.jpg",
        link: amazonLink("1119982626")
      },
      {
        name: "Terraform: Up & Running",
        description: "IaC with Terraform",
        image: "/IMAGE/gadgets/gadget_5.jpg",
        link: amazonLink("1098116747")
      },
      {
        name: "Kubernetes in Action",
        description: "K8s Deep Dive",
        image: "/IMAGE/gadgets/gadget_6.jpg",
        link: amazonLink("1617297615")
      }
    ]
  },
  homelab: {
    title: "Home Lab",
    items: [
      {
        name: "Raspberry Pi 5",
        description: "8GB for Self-hosting",
        image: "/IMAGE/gadgets/gadget_7.jpg",
        link: amazonLink("B0CK2FCG1K")
      },
      {
        name: "Synology DS224+",
        description: "2-Bay NAS Storage",
        image: "/IMAGE/gadgets/gadget_8.jpg",
        link: amazonLink("B0CB3SRC6Z")
      },
      {
        name: "UniFi Dream Machine Pro",
        description: "Network Router & Security",
        image: "/IMAGE/gadgets/gadget_9.jpg",
        link: amazonLink("B086967C9X")
      }
    ]
  },
  peripherals: {
    title: "Essentials",
    items: [
      {
        name: "Kindle Colorsoft Scribe",
        description: "Color E-Reader with Stylus",
        image: "/IMAGE/gadgets/gadget_10.jpg",
        link: amazonLink("B0FC1VJJFP")
      },
      {
        name: "Logitech MX Vertical",
        description: "Ergonomic Wireless Mouse",
        image: "/IMAGE/gadgets/gadget_11.jpg",
        link: amazonLink("B07FNJB8TT")
      },
      {
        name: "Keychron Q1 HE",
        description: "75% Magnetic Switch Keyboard",
        image: "/IMAGE/gadgets/gadget_12.jpg",
        link: amazonLink("B0CR1J2B7B")
      },
      {
        name: "Sony WH-1000XM5",
        description: "Noise Cancelling Headphones",
        image: "/IMAGE/gadgets/gadget_13.jpg",
        link: amazonLink("B09XS7JWHH")
      },
      {
        name: "Elgato Stream Deck MK.2",
        description: "Macro Pad for Productivity",
        image: "/IMAGE/gadgets/gadget_14.jpg",
        link: amazonLink("B09RMXK59C")
      }
    ]
  },
  crypto: {
    title: "Crypto & Security",
    items: [
      {
        name: "Ledger Stax + Recovery Key",
        description: "Premium Wallet Bundle",
        image: "/IMAGE/gadgets/gadget_15.jpg",
        link: "https://shop.ledger.com/products/ledger-stax/ledger-stax%E2%84%A2-+-recovery-key?r=661765d73af1"
      },
      {
        name: "Ledger Nano X",
        description: "Bluetooth Hardware Wallet",
        image: "/IMAGE/gadgets/gadget_16.jpg",
        link: "https://shop.ledger.com/products/ledger-nano-x?r=661765d73af1"
      },
      {
        name: "Ledger Nano S Plus",
        description: "Entry-Level Cold Storage",
        image: "/IMAGE/gadgets/gadget_17.jpg",
        link: "https://shop.ledger.com/products/ledger-nano-s-plus?r=661765d73af1"
      }
    ]
  },
  filming: {
    title: "Motion & Picture",
    items: [
      {
        name: "Sony A7 IV",
        description: "Full Frame Mirrorless Camera",
        image: "/IMAGE/gadgets/gadget_18.jpg",
        link: amazonLink("B09JZT6YK5")
      },
      {
        name: "DJI Osmo Pocket 3",
        description: "Vlogging Camera",
        image: "/IMAGE/gadgets/gadget_19.jpg",
        link: amazonLink("B0CG19QXWD")
      },
      {
        name: "Rode Wireless GO II",
        description: "Wireless Microphone System",
        image: "/IMAGE/gadgets/gadget_20.jpg",
        link: amazonLink("B08XFQ6KP9")
      }
    ]
  }
};

const GearCard = ({ item }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className="gear-card"
    >
      <div className="gear-card-image">
        {!loaded && <div className="skeleton-loader" />}
        <img
          src={item.image}
          alt={item.name}
          onLoad={() => setLoaded(true)}
          style={{ opacity: loaded ? 1 : 0 }}
        />
      </div>
      <div className="gear-card-content">
        <h3 className="gear-card-title">{item.name}</h3>
        <p className="gear-card-description">{item.description}</p>
      </div>
      <ExternalLink className="gear-card-link" size={18} />
    </a>
  );
};

const GearSection = ({ title, items }) => (
  <section className="gear-section">
    <h2 className="gear-section-title">{title}</h2>
    <div className="gear-grid">
      {items.map((item, index) => (
        <GearCard key={index} item={item} />
      ))}
    </div>
  </section>
);

const Gadgets = () => {
  return (
    <>
      <Helmet>
        <title>Gadgets - limkc.com</title>
        <meta name="description" content="Tools, gear, and gadgets I use for development and productivity." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="w-full h-full overflow-auto flex justify-center">
        <div
          className="gadgets-page"
          style={{
            paddingTop: "calc(var(--navbar-height) + 2rem)",
            minHeight: "100vh"
          }}
        >
          {/* Header */}
          <header className="gadgets-header">
            <h1 className="gadgets-title">Gadgets</h1>
            <p className="gadgets-subtitle">My EDC & EDG</p>
          </header>

          {/* Gear Sections */}
          <div className="gadgets-content">
            <GearSection title={gearData.desk.title} items={gearData.desk.items} />
            <GearSection title={gearData.kindle.title} items={gearData.kindle.items} />
            <GearSection title={gearData.homelab.title} items={gearData.homelab.items} />
            <GearSection title={gearData.peripherals.title} items={gearData.peripherals.items} />
            <GearSection title={gearData.crypto.title} items={gearData.crypto.items} />
            <GearSection title={gearData.filming.title} items={gearData.filming.items} />
          </div>
        </div>
      </div>

      <style>{`
        .gadgets-page {
          width: 100%;
          max-width: 900px;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
          padding-bottom: calc(var(--footer-height) + 3rem);
        }

        /* Tablet (iPad Mini 744px - 1024px) needs more bottom padding */
        @media (min-width: 700px) and (max-width: 1024px) {
          .gadgets-page {
            padding-bottom: calc(var(--footer-height) + 8rem) !important;
          }
        }

        .gadgets-header {
          margin-bottom: 3rem;
          text-align: center;
        }

        .gadgets-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .gadgets-subtitle {
          font-size: 1.1rem;
          color: var(--text-secondary);
        }

        .gadgets-content {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .gear-section-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .gear-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .gear-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          text-decoration: none;
          transition: border-color 0.2s, transform 0.2s;
        }

        .gear-card:hover {
          border-color: var(--text-secondary);
          transform: translateY(-2px);
        }

        .gear-card-image {
          position: relative;
          width: 64px;
          height: 64px;
          flex-shrink: 0;
          border-radius: 8px;
          overflow: hidden;
          background: var(--bg-tertiary, #1a1a1a);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .gear-card-image img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 4px;
          transition: opacity 0.3s ease;
        }

        .skeleton-loader {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            var(--bg-tertiary, #1a1a1a) 25%,
            var(--bg-secondary, #2a2a2a) 50%,
            var(--bg-tertiary, #1a1a1a) 75%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 8px;
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .gear-card-content {
          flex: 1;
          min-width: 0;
        }

        .gear-card-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .gear-card-description {
          font-size: 0.85rem;
          color: var(--text-secondary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .gear-card-link {
          flex-shrink: 0;
          color: var(--text-secondary);
          opacity: 0.5;
          transition: opacity 0.2s;
        }

        .gear-card:hover .gear-card-link {
          opacity: 1;
        }

        .gadgets-page {
          padding-bottom: 2rem;
        }

        @media (max-width: 640px) {
          .gadgets-page {
            padding-bottom: calc(var(--footer-height-mobile-portrait) + 2rem);
          }

          .gear-grid {
            grid-template-columns: 1fr;
          }

          .gadgets-title {
            font-size: 2rem;
          }

          .gear-card-image {
            width: 56px;
            height: 56px;
          }
        }
      `}</style>
    </>
  );
};

export default Gadgets;
